/**
 * Production adapter for `zoom_view`: binds the React-free ports
 * ({@link ZoomViewDeps}) to existing viewer calls only —
 *  - focus: `useViewportFocus().focusOnStructure` (centres and zooms all
 *    three views on the structure's contour extent),
 *  - show / select: the same domain events the structure list publishes
 *    (`RoiVisibilityChangeRequestedEvent`, `RoiSelectionRequestedEvent`),
 *  - zoom step: `PanZoom.zoomIn` / `zoomOut` on the main view, the mouse
 *    wheel's step, with its own zoom limits.
 * Like the other ops' engineDeps, this browser glue is covered by the manual
 * demo; the logic it feeds is unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import {
    domainEventBus,
    RoiSelectionRequestedEvent,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import useViewportFocus from '~/hooks/useViewportFocus';
import { environmentState, mainViewState } from '~/state/atoms/engineAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { projectStructureFacts } from '../core/engineStructureFacts';
import type { ZoomViewDeps } from './zoomView';

function publish(event: Parameters<typeof domainEventBus.publish>[0]): void {
    domainEventBus.publish(event).catch((error) => {
        console.error('zoom_view: failed to publish event:', error);
    });
}

export function useZoomViewDeps(): () => ZoomViewDeps {
    const env = useRecoilValue(environmentState);
    const mainView = useRecoilValue(mainViewState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const selectedRoi = useRecoilValue(selectedROIState);
    const { focusOnStructure } = useViewportFocus();

    return useCallback(
        (): ZoomViewDeps => ({
            viewReady: !!env?.image && !!mainView,
            structures: projectStructureFacts(structureSet, env),
            isVisible: (roiNumber) =>
                structureSet?.roiArray?.find((r) => r.roiNumber === roiNumber)
                    ?.visible ?? false,
            isSelected: (roiNumber) => selectedRoi === roiNumber,
            show: (roiNumber) =>
                publish(new RoiVisibilityChangeRequestedEvent(roiNumber, true)),
            // Selecting an already-selected ROI toggles it off, so the port
            // is only called when it is not selected (see zoomView).
            select: (roiNumber) =>
                publish(new RoiSelectionRequestedEvent(roiNumber)),
            focus: (roiNumber) => focusOnStructure(roiNumber),
            zoomStep: (direction) => {
                if (!env || !mainView) {
                    return false;
                }
                const before = env.panZoom.scale;
                if (direction === 'in') {
                    env.panZoom.zoomIn(mainView, undefined);
                } else {
                    env.panZoom.zoomOut(mainView, undefined);
                }
                return env.panZoom.scale !== before;
            },
        }),
        [env, mainView, structureSet, selectedRoi, focusOnStructure]
    );
}
